// eslint-disable-next-line import/no-cycle
import { getInitMarginRatio } from '@/features/markets/utils/helpers'; // eslint-disable-line import/no-restricted-paths
// eslint-disable-next-line import/no-cycle
import { getEstFillPrice, getFee } from '@/features/trade/order/utils/math'; // eslint-disable-line import/no-restricted-paths
import { getDefaultBigDecimal } from '@/lib/utils';
import {
  Account,
  BasePosition,
  getCurrentAccount,
  Position,
  Positions,
  useAccountStore,
} from '@/store/use-account-store';
import {
  COLLATERAL_DECIMALS,
  CUM_FUNDING_DECIMALS,
  getMarketStore,
  MarketData,
  MarketSpec,
  OrderBook,
} from '@/store/use-markets-store';
// eslint-disable-next-line import/no-cycle
import { FilledOrder, useOrdersStore } from '@/store/use-orders-store';
import { OrderType, PnlType } from '@/types';
import {
  adjustDecimals,
  BigDecimal,
  bigIntToDecimalStr,
} from '@/utils/value-format';

export interface MarketInfo {
  marketData: MarketData;
  marketSpec: MarketSpec;
  book: OrderBook;
}

export interface UnrealizedPnL {
  pnl: BigDecimal;
  roePct: BigDecimal;
}

export interface PositionPnLOptions {
  isLong: boolean;
  size: bigint;
  cost: bigint;
  entryFunding: bigint;
  initMarginRatio: bigint;
  price?: bigint;
}

export function getAccountHealthPct(
  account?: Pick<Account, 'value' | 'maintMarginReq'>,
): BigDecimal {
  // Returns 2 decimal places (%)
  const { value, maintMarginReq } = account ?? getCurrentAccount<Account>();
  if (value.bigint <= 0n) return getDefaultBigDecimal(2);
  if (maintMarginReq.bigint === 0n)
    return { bigint: 10n ** 4n, decimal: bigIntToDecimalStr(10n ** 4n, 2n) };
  const healthPct =
    ((value.bigint > maintMarginReq.bigint
      ? value.bigint - maintMarginReq.bigint
      : 0n) *
      10n ** 4n) /
    maintMarginReq.bigint;
  const boundHealthPct = healthPct > 10n ** 4n ? 10n ** 4n : healthPct;
  return {
    bigint: boundHealthPct,
    decimal: bigIntToDecimalStr(boundHealthPct, 2n),
  };
}

export function getAccountLeverage(
  account?: Pick<Account, 'value'> & { positions?: Positions },
): BigDecimal {
  const value = account?.value ?? getCurrentAccount().value;

  if (value.bigint === 0n)
    return { bigint: 0n, decimal: bigIntToDecimalStr(0n, 2n) };

  const notionalValue = getAccountNotionalValue(account?.positions).bigint;
  // if (notionalValue <= accountValue) {
  //   return { bigint: 0n, decimal: bigIntToDecimalStr(0n, 2n) };
  // }
  const leverage = (notionalValue * 10n ** 4n) / value.bigint / 10n ** 2n;
  return {
    bigint: leverage,
    decimal: bigIntToDecimalStr(leverage, 2n),
  };
}

function getAccountNotionalValue(positions?: Positions): BigDecimal {
  const accountPositions = positions ?? getCurrentAccount().positions;
  const notionalValue = Object.entries(accountPositions).reduce(
    (acc, [symbol, pos]) => {
      const { marketData, marketSpec } = getMarketStore(symbol).getState();
      return (
        acc +
        adjustDecimals(
          pos.size * marketData.markPrice,
          COLLATERAL_DECIMALS -
            marketSpec.sizeDecimals -
            marketSpec.priceDecimals,
        )
      );
    },
    BigInt(0),
  );
  return {
    bigint: notionalValue,
    decimal: bigIntToDecimalStr(notionalValue, COLLATERAL_DECIMALS),
  };
}

export const getAccountMaintMarginReq = (positions?: Positions): BigDecimal => {
  const maintMarginReq = Object.entries(
    positions ?? getCurrentAccount().positions,
  ).reduce((acc, [symbol, pos]) => {
    const { marketData, marketSpec } = getMarketStore(symbol).getState();
    return (
      acc +
      adjustDecimals(
        pos.size *
          marketData.markPrice *
          (pos.initMarginRatio / 2n < marketSpec.maintMarginRatio
            ? pos.initMarginRatio / 2n
            : marketSpec.maintMarginRatio),
        COLLATERAL_DECIMALS -
          marketSpec.sizeDecimals -
          marketSpec.priceDecimals -
          4n,
      )
    );
  }, 0n);
  return {
    bigint: maintMarginReq,
    decimal: bigIntToDecimalStr(maintMarginReq, COLLATERAL_DECIMALS),
  };
};

export function getAccountValue(
  account?: Pick<Account, 'collateral' | 'unrealizedPnl'>,
): BigDecimal {
  const { collateral, unrealizedPnl } = account ?? getCurrentAccount<Account>();
  const value = collateral + unrealizedPnl[PnlType.NET_MARKET].bigint;
  return {
    bigint: value,
    decimal: bigIntToDecimalStr(value, COLLATERAL_DECIMALS),
  };
}

export const getAccountPnl = (
  positions?: Positions,
): Account['unrealizedPnl'] => {
  const pos = positions ?? getCurrentAccount().positions;

  const cumulatePnl = (type: PnlType) =>
    Object.values(pos).reduce(
      (acc, { unrealizedPnl }) => acc + unrealizedPnl[type].pnl.bigint,
      0n,
    );

  const netPnl = cumulatePnl(PnlType.NET);
  const marketPnl = cumulatePnl(PnlType.MARKET);
  const netMarketPnl = cumulatePnl(PnlType.NET_MARKET);

  return {
    [PnlType.NET]: {
      bigint: netPnl,
      decimal: bigIntToDecimalStr(netPnl, COLLATERAL_DECIMALS),
    },
    [PnlType.MARKET]: {
      bigint: marketPnl,
      decimal: bigIntToDecimalStr(marketPnl, COLLATERAL_DECIMALS),
    },
    [PnlType.NET_MARKET]: {
      bigint: netMarketPnl,
      decimal: bigIntToDecimalStr(netMarketPnl, COLLATERAL_DECIMALS),
    },
  };
};

const getGrossPnl = (
  symbol: string,
  price: bigint,
  { isLong, cost, size }: Pick<BasePosition, 'isLong' | 'size' | 'cost'>,
) => {
  const { marketSpec } = getMarketStore(symbol).getState();
  return (
    (isLong ? 1n : -1n) *
    adjustDecimals(
      size * price - cost,
      COLLATERAL_DECIMALS - marketSpec.sizeDecimals - marketSpec.priceDecimals,
    )
  );
};

export const getPnlRoePct = (pnl: bigint, initMargin: bigint): BigDecimal => {
  const roePct = initMargin !== 0n ? (pnl * 10n ** 4n) / initMargin : 0n;
  return {
    bigint: roePct,
    decimal: bigIntToDecimalStr(roePct, 2n),
  };
};

export const getPositionUnrealizedPnL = (
  symbol: string,
  estFillPrice: bigint,
  position?: Pick<
    Position,
    'isLong' | 'size' | 'cost' | 'funding' | 'fee' | 'initMargin'
  >,
): Record<PnlType, UnrealizedPnL> => {
  const { marketData } = getMarketStore(symbol).getState();

  const { fee, funding, initMargin, isLong, size, cost } =
    position ?? getCurrentAccount<Position>((state) => state.positions[symbol]);

  const marketPnl = getGrossPnl(symbol, marketData.markPrice, {
    isLong,
    cost,
    size,
  });
  const netPnl =
    getGrossPnl(symbol, estFillPrice, { isLong, cost, size }) + funding - fee;
  const netMarketPnl =
    getGrossPnl(symbol, marketData.markPrice, { isLong, cost, size }) +
    funding -
    fee;

  return {
    [PnlType.NET]: {
      pnl: {
        bigint: netPnl,
        decimal: bigIntToDecimalStr(netPnl, COLLATERAL_DECIMALS),
      },
      roePct: getPnlRoePct(netPnl, initMargin),
    },
    [PnlType.MARKET]: {
      pnl: {
        bigint: marketPnl,
        decimal: bigIntToDecimalStr(marketPnl, COLLATERAL_DECIMALS),
      },
      roePct: getPnlRoePct(marketPnl, initMargin),
    },
    [PnlType.NET_MARKET]: {
      pnl: {
        bigint: netMarketPnl,
        decimal: bigIntToDecimalStr(netMarketPnl, COLLATERAL_DECIMALS),
      },
      roePct: getPnlRoePct(netMarketPnl, initMargin),
    },
  };
};

export const getPositionFunding = (
  symbol: string,
  isLong: boolean,
  size: bigint,
  entryFunding: bigint,
) => {
  const { marketData, marketSpec } = getMarketStore(symbol).getState();

  if (!marketData.cumFunding) return 0n;

  const funding = adjustDecimals(
    size * (marketData.cumFunding - entryFunding),
    COLLATERAL_DECIMALS - marketSpec.sizeDecimals - CUM_FUNDING_DECIMALS,
  );
  const signedFunding = (isLong ? -1n : 1n) * funding; // if long and funding is positive, then trader is paying

  return signedFunding;
};

// cross margined
export const getPositionLiqPrice = (
  symbol: string,
  account?: Pick<Account, 'positions' | 'value' | 'maintMarginReq'>,
): BigDecimal => {
  const { marketData, marketSpec } = getMarketStore(symbol).getState();

  const { positions, value, maintMarginReq } =
    account ?? getCurrentAccount<Account>();
  const pos = positions[symbol];

  // Returns priceDecimals
  if (!pos || pos.size == 0n)
    return {
      bigint: 0n,
      decimal: bigIntToDecimalStr(0n, marketSpec.priceDecimals),
    };

  const direction = pos.isLong ? 1n : -1n;

  const accValWithoutPos =
    value.bigint - pos.unrealizedPnl[PnlType.MARKET].pnl.bigint;

  const maintMarginReqWithoutPos =
    maintMarginReq.bigint -
    adjustDecimals(
      marketData.markPrice *
        pos.size *
        (pos.initMarginRatio / 2n < marketSpec.maintMarginRatio
          ? pos.initMarginRatio / 2n
          : marketSpec.maintMarginRatio),
      COLLATERAL_DECIMALS -
        marketSpec.priceDecimals -
        marketSpec.sizeDecimals -
        4n,
    );

  const numerator =
    adjustDecimals(
      maintMarginReqWithoutPos - accValWithoutPos,
      marketSpec.sizeDecimals +
        marketSpec.priceDecimals +
        4n -
        COLLATERAL_DECIMALS,
    ) + adjustDecimals(direction * pos.cost, 4n); // sizeDecimals + priceDecimals + 4

  const denominator =
    direction * pos.size * 10n ** 4n -
    pos.size *
      (pos.initMarginRatio / 2n < marketSpec.maintMarginRatio
        ? pos.initMarginRatio / 2n
        : marketSpec.maintMarginRatio); // sizeDecimals + 4

  let liqPrice = numerator / denominator; // priceDecimals
  if (pos.isLong) {
    liqPrice =
      liqPrice > marketData.markPrice ? marketData.markPrice : liqPrice; // min
  } else {
    liqPrice =
      liqPrice < marketData.markPrice ? marketData.markPrice : liqPrice; // max
  }

  liqPrice = liqPrice < 0n ? 0n : liqPrice;
  return {
    bigint: liqPrice,
    decimal: bigIntToDecimalStr(liqPrice, marketSpec.priceDecimals),
  };
};

export const getRealizedPnl = (
  symbol: string,
  order: Pick<
    FilledOrder,
    'isBuy' | 'settledFunding' | 'fees' | 'lastFilledPrice' | 'lastFilledSize'
  >,
  pos?: Pick<Position, 'size' | 'isLong' | 'cost'>,
): BigDecimal => {
  if (!pos || pos.size === 0n || pos.isLong === order.isBuy)
    return {
      bigint: order.settledFunding - order.fees,
      decimal: bigIntToDecimalStr(
        order.settledFunding - order.fees,
        COLLATERAL_DECIMALS,
      ),
    };

  const cost =
    order.lastFilledSize === pos.size
      ? pos.cost
      : (pos.cost * order.lastFilledSize) / pos.size;

  const { marketSpec } = getMarketStore(symbol).getState();

  const realizedPnl =
    (pos.isLong ? 1n : -1n) *
    adjustDecimals(
      order.lastFilledSize * order.lastFilledPrice - cost,
      COLLATERAL_DECIMALS - marketSpec.sizeDecimals - marketSpec.priceDecimals,
    );
  return {
    bigint: realizedPnl + order.settledFunding - order.fees,
    decimal: bigIntToDecimalStr(
      realizedPnl + order.settledFunding - order.fees,
      COLLATERAL_DECIMALS,
    ),
  };
};

export const getSettledFunding = (
  symbol: string,
  account: Account,
  options?: {
    cumFunding?: bigint;
  },
): BigDecimal => {
  const pos = account.positions[symbol];
  if (!pos || pos.size === 0n)
    return { bigint: 0n, decimal: bigIntToDecimalStr(0n, COLLATERAL_DECIMALS) };

  const { marketData, marketSpec } = getMarketStore(symbol).getState();

  const cumFunding = options?.cumFunding ?? marketData.cumFunding ?? 0n;

  const settledFunding =
    (pos.isLong ? -1n : 1n) *
    adjustDecimals(
      pos.size * (cumFunding - (pos.entryFunding ?? 0n)),
      COLLATERAL_DECIMALS - marketSpec.sizeDecimals - CUM_FUNDING_DECIMALS,
      'ceil',
    );

  return {
    bigint: settledFunding,
    decimal: bigIntToDecimalStr(settledFunding, COLLATERAL_DECIMALS),
  };
};

export const getUpdatedAccountOnFill = (
  symbol: string,
  isBuy: boolean,
  filledSize: bigint,
  filledPrice: bigint,
  realizedPnl: bigint,
  orderInitMarginRatio: bigint,
  account: { collateral: bigint; positions: Record<string, BasePosition> },
  options?: {
    address?: string;
    cumFunding?: bigint;
  },
): Omit<
  Account,
  'connections' | 'lastUpdated' | 'hasHydrated' | 'nonWithdrawable'
> => {
  const address = options?.address ?? useAccountStore.getState().address;
  // Updates collateral and positions
  const pos = account.positions[symbol] as Position;
  const positions = { ...account.positions } as Record<string, Position>;

  const { marketData, book, marketSpec } = getMarketStore(symbol).getState();

  const entryFunding = options?.cumFunding ?? marketData.cumFunding ?? 0n;

  if (!pos) {
    const fee = getFee(symbol, filledSize, filledPrice, { address });
    const funding = getPositionFunding(symbol, isBuy, filledSize, entryFunding);
    const cost = filledPrice * filledSize;
    const initMargin = getPositionInitMargin(
      symbol,
      cost,
      orderInitMarginRatio,
    ).bigint;

    const estFillPrice =
      getEstFillPrice(!isBuy, filledSize, book, marketSpec.priceDecimals)
        ?.bigint ?? marketData.markPrice;

    const unrealizedPnl = getPositionUnrealizedPnL(symbol, estFillPrice, {
      isLong: isBuy,
      size: filledSize,
      initMargin,
      funding,
      fee,
      cost,
    });
    // no position
    positions[symbol] = {
      isLong: isBuy,
      size: filledSize,
      cost,
      entryFunding,
      initMarginRatio: orderInitMarginRatio,
      fee,
      funding,
      initMargin,
      unrealizedPnl,
      liqPrice: getDefaultBigDecimal(marketSpec.priceDecimals), // TODO: calculate
      value: getPositionValue(symbol, filledSize),
    };
  } else if (pos.isLong !== isBuy && pos.size === filledSize) {
    delete positions[symbol];
  } else {
    if (pos.isLong === isBuy) {
      // increase position
      positions[symbol] = {
        ...pos,
        size: pos.size + filledSize,
        cost: pos.cost + filledPrice * filledSize,
        entryFunding,
        initMarginRatio:
          (pos.initMarginRatio * pos.size + orderInitMarginRatio * filledSize) /
          (pos.size + filledSize),
      };
    } else if (pos.size >= filledSize) {
      // decrease position
      positions[symbol] = {
        ...pos,
        size: pos.size - filledSize,
        cost: pos.cost - (pos.cost * filledSize) / pos.size,
        entryFunding,
      };
    } else {
      // cross position
      positions[symbol] = {
        ...pos,
        isLong: !pos.isLong,
        size: filledSize - pos.size,
        cost: filledPrice * (filledSize - pos.size),
        entryFunding,
        initMarginRatio: orderInitMarginRatio,
      };
    }
    const fee = getFee(symbol, positions[symbol].size, filledPrice, {
      address,
    });
    const funding = getPositionFunding(
      symbol,
      positions[symbol].isLong,
      positions[symbol].size,
      entryFunding,
    );

    const initMargin = getPositionInitMargin(
      symbol,
      positions[symbol].cost,
      positions[symbol].initMarginRatio,
    ).bigint;

    positions[symbol] = {
      ...positions[symbol],
      fee,
      funding,
      initMargin,
      liqPrice: getDefaultBigDecimal(marketSpec.priceDecimals), // TODO: calculate
      value: getPositionValue(symbol, positions[symbol].size),
    };

    const estFillPrice =
      getEstFillPrice(
        !positions[symbol].isLong,
        positions[symbol].size,
        book,
        marketSpec.priceDecimals,
      )?.bigint ?? marketData.markPrice;

    const unrealizedPnl = getPositionUnrealizedPnL(
      symbol,
      estFillPrice,
      positions[symbol],
    );

    positions[symbol] = {
      ...positions[symbol],
      unrealizedPnl,
    };
  }

  const collateral = account.collateral + realizedPnl;
  const unrealizedPnl = getAccountPnl(positions);
  const value = getAccountValue({
    collateral,
    unrealizedPnl,
  });
  const availableFunds = getAvailableFunds({
    address,
    account: {
      collateral,
      unrealizedPnl,
      positions,
    },
  });

  const leverage = getAccountLeverage({ value, positions });
  const maintMarginReq = getAccountMaintMarginReq(positions);
  const healthPct = getAccountHealthPct({ value, maintMarginReq });

  return {
    value,
    availableFunds,
    unrealizedPnl,
    leverage,
    collateral,
    positions,
    maintMarginReq,
    healthPct,
  };
};

export const getAvailableFunds = (options?: {
  account?: Pick<Account, 'collateral' | 'positions' | 'unrealizedPnl'>;
  address?: string;
}): BigDecimal => {
  const { address: storeAddress } = useAccountStore.getState();
  const address = options?.address ?? storeAddress;
  const {
    collateral,
    positions,
    unrealizedPnl: { [PnlType.NET_MARKET]: unrealizedPnl },
  } = options?.account ?? getCurrentAccount<Account>(undefined, address);

  const totalPosInitMargin = Object.values(positions).reduce(
    (acc, pos) => acc + (pos.value.bigint * pos.initMarginRatio) / 10n ** 4n,
    0n,
  );

  const { orders, newOrderIds } = useOrdersStore.getState().getOrders(address);

  const totalOrderInitMargin = Array.from(newOrderIds, (id) => orders[id])
    // .filter((order) => !order.reduceOnly)
    .filter(
      (order) =>
        order && order.orderType === OrderType.LIMIT && !order.reduceOnly,
    )
    .reduce((acc, order) => {
      const { priceDecimals, sizeDecimals } = getMarketStore(
        order.symbol,
      ).getState().marketSpec;
      return (
        acc +
        adjustDecimals(
          order.size *
            order.limitPrice *
            (order.initMarginRatio ??
              getInitMarginRatio(order.symbol, address)),
          COLLATERAL_DECIMALS - sizeDecimals - priceDecimals - 4n,
          'ceil',
        )
      );
    }, 0n);

  let availableFunds = collateral - totalPosInitMargin - totalOrderInitMargin;

  if (unrealizedPnl.bigint < 0n) {
    availableFunds += unrealizedPnl.bigint;
  }

  return {
    bigint: availableFunds,
    decimal: bigIntToDecimalStr(availableFunds, COLLATERAL_DECIMALS),
  };
};

export const getPositionInitMargin = (
  symbol: string,
  cost: bigint,
  initMarginRatio: bigint,
): BigDecimal => {
  const { marketSpec } = getMarketStore(symbol).getState();

  const initMargin = adjustDecimals(
    cost * initMarginRatio,
    COLLATERAL_DECIMALS -
      marketSpec.sizeDecimals -
      marketSpec.priceDecimals -
      4n,
  );

  return {
    bigint: initMargin,
    decimal: bigIntToDecimalStr(initMargin, COLLATERAL_DECIMALS),
  };
};

export const getPositionValue = (symbol: string, size?: bigint): BigDecimal => {
  const positionSize =
    size ?? getCurrentAccount((state) => state.positions[symbol]?.size);
  if (!positionSize) return getDefaultBigDecimal();
  const { marketData, marketSpec } = getMarketStore(symbol).getState();
  const value = adjustDecimals(
    positionSize * marketData.markPrice,
    COLLATERAL_DECIMALS - marketSpec.priceDecimals - marketSpec.sizeDecimals,
  );
  return {
    bigint: value,
    decimal: bigIntToDecimalStr(value, COLLATERAL_DECIMALS),
  };
};

export const getOrderEntryPrice = (order: FilledOrder) => {
  return (
    (order.realizedPnl + order.fees - order.settledFunding) /
      (order.size * (order.isBuy ? 1n : -1n)) +
    order.lastFilledPrice
  );
};
